import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, ArrowLeft, X, Radio, Upload, Bell } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";

const EMPTY = {
  title: "", description: "", category: "Originals", thumbnail: "",
  source_type: "youtube", source_url: "", duration: "",
  is_premium: false, featured: false,
  ppv: false, ppv_price: 9.99, ppv_type: "rental", rental_hours: 48, event_start: "",
};

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | video
  const [form, setForm] = useState(EMPTY);
  const [liveOpen, setLiveOpen] = useState(false);
  const [channels, setChannels] = useState([]);
  const [chForm, setChForm] = useState(null); // null | 'new' | channel
  const CH_EMPTY = { name: "", logo: "", category: "General", source_type: "hls", source_url: "", is_live: true, order: 0 };
  const [chData, setChData] = useState(CH_EMPTY);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlist, setWaitlist] = useState(null);
  const [launching, setLaunching] = useState("");

  const openWaitlist = async () => {
    setWaitlistOpen(true);
    setWaitlist(null);
    try {
      const { data } = await api.get("/admin/notify");
      setWaitlist(data);
    } catch {
      toast.error("Could not load waitlist");
    }
  };

  const launchNotify = async (show) => {
    const label = show === "all" ? "everyone on the waitlist" : `the ${show} waitlist`;
    if (!window.confirm(`Email ${label} that it's live now?`)) return;
    setLaunching(show);
    try {
      const { data } = await api.post("/admin/notify/launch", { show });
      toast.success(
        data.count > 0
          ? `Launch alert queued to ${data.count} fan(s)`
          : "Everyone on this list has already been notified"
      );
      await openWaitlist();
    } catch {
      toast.error("Could not send launch alert");
    } finally {
      setLaunching("");
    }
  };

  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const handleUpload = async (file, kind) => {
    const setBusy = kind === "video" ? setUploadingVideo : setUploadingThumb;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const absoluteUrl = `${BACKEND}${data.url}`;
      if (kind === "video") {
        setForm((f) => ({ ...f, source_type: "file", source_url: absoluteUrl }));
      } else {
        setForm((f) => ({ ...f, thumbnail: absoluteUrl }));
      }
      toast.success(`${kind === "video" ? "Video" : "Image"} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) {
      navigate("/");
    }
  }, [user, loading, navigate]);

  const load = async () => {
    const { data } = await api.get("/videos");
    setVideos(data);
  };
  useEffect(() => {
    load();
  }, []);

  const loadChannels = async () => {
    try {
      const { data } = await api.get("/admin/live-channels");
      setChannels(data);
    } catch {}
  };
  useEffect(() => {
    loadChannels();
  }, []);

  const saveChannel = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...chData };
      delete payload.id;
      delete payload.created_at;
      if (chForm === "new") await api.post("/admin/live-channels", payload);
      else await api.put(`/admin/live-channels/${chForm.id}`, payload);
      toast.success("Channel saved");
      setChForm(null);
      loadChannels();
    } catch {
      toast.error("Could not save channel");
    }
  };

  const deleteChannel = async (ch) => {
    if (!window.confirm(`Delete channel "${ch.name}"?`)) return;
    await api.delete(`/admin/live-channels/${ch.id}`);
    toast.success("Channel deleted");
    loadChannels();
  };

  const uploadChannelLogo = async (file) => {
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setChData((f) => ({ ...f, logo: `${BACKEND}${data.url}` }));
      toast.success("Logo uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploadingLogo(false);
    }
  };

  const openNew = () => {
    setForm(EMPTY);
    setEditing("new");
  };
  const openEdit = (v) => {
    setForm({ ...EMPTY, ...v });
    setEditing(v);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      delete payload.id;
      delete payload.locked;
      delete payload.created_at;
      if (editing === "new") {
        await api.post("/admin/videos", payload);
        toast.success("Clip added");
      } else {
        await api.put(`/admin/videos/${editing.id}`, payload);
        toast.success("Clip updated");
      }
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete "${v.title}"?`)) return;
    await api.delete(`/admin/videos/${v.id}`);
    toast.success("Deleted");
    load();
  };

  if (loading || !user || user.role !== "admin") return null;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="flex items-center justify-between px-6 md:px-12 h-20 border-b border-white/10">
        <div className="flex items-center gap-4">
          <img src={LOGO_URL} alt="IMN" className="h-8 w-auto object-contain" />
          <span className="font-display text-xl font-bold uppercase tracking-widest">
            Admin
          </span>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/")}
            data-testid="admin-back-btn"
            className="flex items-center gap-2 font-mono-imn text-xs uppercase tracking-widest text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft size={14} /> Site
          </button>
          <button
            onClick={() => setLiveOpen(true)}
            data-testid="admin-live-btn"
            className="flex items-center gap-2 border border-white/20 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-white/10 transition-colors"
          >
            <Radio size={14} /> Local Cable
          </button>
          <button
            onClick={openWaitlist}
            data-testid="admin-waitlist-btn"
            className="flex items-center gap-2 border border-white/20 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-white/10 transition-colors"
          >
            <Bell size={14} /> Waitlist
          </button>
          <button
            onClick={openNew}
            data-testid="admin-add-btn"
            className="flex items-center gap-2 bg-white text-black px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-white/90 active:scale-95 transition-all"
          >
            <Plus size={14} /> Add Clip
          </button>
        </div>
      </header>

      <main className="px-6 md:px-12 py-10">
        <p className="font-mono-imn text-xs tracking-[0.3em] text-gold uppercase mb-6">
          {videos.length} Clips · Manage Library
        </p>
        <div className="border border-white/10">
          {videos.map((v, i) => (
            <div
              key={v.id}
              data-testid={`admin-row-${v.id}`}
              className={`flex items-center gap-4 p-4 ${
                i !== videos.length - 1 ? "border-b border-white/10" : ""
              } hover:bg-white/[0.02] transition-colors`}
            >
              <img src={v.thumbnail} alt="" className="w-28 aspect-video object-cover border border-white/10 grayscale" />
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-lg uppercase truncate">{v.title}</h3>
                <p className="font-mono-imn text-[11px] tracking-widest uppercase text-white/40">
                  {v.category} · {v.is_premium ? "Premium" : "Free"}
                  {v.featured ? " · Featured" : ""} · {v.duration || "--:--"}
                </p>
              </div>
              <button
                onClick={() => openEdit(v)}
                data-testid={`admin-edit-${v.id}`}
                className="p-2 border border-white/15 hover:bg-white/10 transition-colors"
              >
                <Edit3 size={16} />
              </button>
              <button
                onClick={() => remove(v)}
                data-testid={`admin-delete-${v.id}`}
                className="p-2 border border-white/15 hover:bg-red-500/20 hover:border-red-500/40 transition-colors"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </main>

      {editing && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 overflow-y-auto">
          <form
            onSubmit={save}
            className="relative w-full max-w-lg border border-white/10 bg-[#0A0A0A] p-8 my-8"
            data-testid="admin-form"
          >
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="absolute right-4 top-4 text-white/50 hover:text-white"
            >
              <X size={20} />
            </button>
            <h2 className="font-display text-3xl font-bold uppercase mb-6">
              {editing === "new" ? "Add Clip" : "Edit Clip"}
            </h2>
            <div className="space-y-3">
              <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="form-title" required />
              <div>
                <label className="font-mono-imn text-[10px] tracking-widest uppercase text-white/50">Description</label>
                <textarea
                  data-testid="form-description"
                  className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:border-gold transition-colors mt-1"
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono-imn text-[10px] tracking-widest uppercase text-white/50">Category</label>
                  <select
                    data-testid="form-category"
                    className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:border-gold mt-1"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                  >
                    <option className="bg-black">Originals</option>
                    <option className="bg-black">Free Previews</option>
                    <option className="bg-black">Premium Originals</option>
                    <option className="bg-black">The Casting Room</option>
                    <option className="bg-black">Get Fit With Apple Watts</option>
                    <option className="bg-black">The Variable X</option>
                    <option className="bg-black">Podcast</option>
                    <option className="bg-black">Pay-Per-View</option>
                    <option className="bg-black">Coming Soon</option>
                  </select>
                </div>
                <div>
                  <label className="font-mono-imn text-[10px] tracking-widest uppercase text-white/50">Source Type</label>
                  <select
                    data-testid="form-source-type"
                    className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:border-gold mt-1"
                    value={form.source_type}
                    onChange={(e) => setForm({ ...form, source_type: e.target.value })}
                  >
                    <option className="bg-black" value="youtube">YouTube</option>
                    <option className="bg-black" value="vimeo">Vimeo</option>
                    <option className="bg-black" value="instagram">Instagram</option>
                    <option className="bg-black" value="url">Direct URL / File</option>
                    <option className="bg-black" value="file">Uploaded File</option>
                  </select>
                </div>
              </div>
              <div>
                <Field label="Embed / Video URL" value={form.source_url} onChange={(v) => setForm({ ...form, source_url: v })} testid="form-source-url" placeholder="https://www.youtube.com/embed/VIDEO_ID" />
                <label className="mt-2 inline-flex items-center gap-2 text-[11px] font-mono-imn uppercase tracking-widest text-gold cursor-pointer hover:opacity-80" data-testid="upload-video-label">
                  <Upload size={13} /> {uploadingVideo ? "Uploading…" : "Or upload a video file"}
                  <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden"
                    data-testid="upload-video-input"
                    onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0], "video")} />
                </label>
              </div>
              <div>
                <Field label="Thumbnail URL" value={form.thumbnail} onChange={(v) => setForm({ ...form, thumbnail: v })} testid="form-thumbnail" />
                <label className="mt-2 inline-flex items-center gap-2 text-[11px] font-mono-imn uppercase tracking-widest text-gold cursor-pointer hover:opacity-80" data-testid="upload-thumb-label">
                  <Upload size={13} /> {uploadingThumb ? "Uploading…" : "Or upload an image"}
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                    data-testid="upload-thumb-input"
                    onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0], "thumb")} />
                </label>
              </div>
              <Field label="Duration" value={form.duration} onChange={(v) => setForm({ ...form, duration: v })} testid="form-duration" placeholder="12:34" />
              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="form-premium">
                  <input type="checkbox" checked={form.is_premium} onChange={(e) => setForm({ ...form, is_premium: e.target.checked })} className="accent-white" />
                  Premium (locked)
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="form-featured">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-white" />
                  Featured
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer text-gold" data-testid="form-ppv">
                  <input type="checkbox" checked={form.ppv} onChange={(e) => setForm({ ...form, ppv: e.target.checked })} className="accent-yellow-600" />
                  Pay-Per-View
                </label>
              </div>

              {form.ppv && (
                <div className="border border-gold/30 bg-gold/[0.04] p-4 space-y-3">
                  <p className="font-mono-imn text-[10px] tracking-widest uppercase text-gold">PPV Settings</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Price (USD)" value={form.ppv_price} onChange={(v) => setForm({ ...form, ppv_price: parseFloat(v) || 0 })} testid="form-ppv-price" placeholder="9.99" />
                    <div>
                      <label className="font-mono-imn text-[10px] tracking-widest uppercase text-white/50">PPV Type</label>
                      <select
                        data-testid="form-ppv-type"
                        className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:border-gold mt-1"
                        value={form.ppv_type}
                        onChange={(e) => setForm({ ...form, ppv_type: e.target.value })}
                      >
                        <option className="bg-black" value="rental">Timed Rental</option>
                        <option className="bg-black" value="event">Live Event Ticket</option>
                      </select>
                    </div>
                  </div>
                  {form.ppv_type === "rental" ? (
                    <Field label="Rental window (hours)" value={form.rental_hours} onChange={(v) => setForm({ ...form, rental_hours: parseInt(v) || 48 })} testid="form-rental-hours" placeholder="48" />
                  ) : (
                    <Field label="Event start (optional)" value={form.event_start} onChange={(v) => setForm({ ...form, event_start: v })} testid="form-event-start" placeholder="2026-08-01 20:00" />
                  )}
                </div>
              )}
            </div>
            <button
              type="submit"
              data-testid="admin-save-btn"
              className="w-full mt-6 bg-white text-black font-semibold py-3 text-sm uppercase tracking-wider hover:bg-white/90 active:scale-[0.98] transition-all"
            >
              Save Clip
            </button>
          </form>
        </div>
      )}

      {liveOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 overflow-y-auto">
          <div className="relative w-full max-w-2xl border border-white/10 bg-[#0A0A0A] p-8 my-8" data-testid="live-manager">
            <button type="button" onClick={() => { setLiveOpen(false); setChForm(null); }} className="absolute right-4 top-4 text-white/50 hover:text-white">
              <X size={20} />
            </button>
            <h2 className="font-display text-3xl font-bold uppercase mb-1 flex items-center gap-2">
              <Radio size={24} className="text-gold" /> Local Cable Channels
            </h2>
            <p className="text-white/40 text-xs mb-6">Live Pass holders only ($7.99/mo). Add each local network / channel with an HLS .m3u8 or YouTube Live URL you're legally authorized to carry (your own feeds, or networks you have a distribution/carriage agreement with).</p>

            {!chForm ? (
              <>
                <div className="border border-white/10 mb-4">
                  {channels.length === 0 && <p className="p-4 text-white/40 text-sm text-center">No channels yet.</p>}
                  {channels.map((ch, i) => (
                    <div key={ch.id} data-testid={`channel-row-${ch.id}`} className={`flex items-center gap-3 p-3 ${i !== channels.length - 1 ? "border-b border-white/10" : ""}`}>
                      {ch.logo ? <img src={ch.logo} alt="" className="w-12 h-12 object-cover border border-white/10" /> : <div className="w-12 h-12 bg-white/5 border border-white/10" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-display uppercase truncate">{ch.name}</p>
                        <p className="font-mono-imn text-[10px] tracking-widest uppercase text-white/40">{ch.category} · {ch.source_type} · {ch.is_live ? "Live" : "Off"}</p>
                      </div>
                      <button onClick={() => { setChData({ ...CH_EMPTY, ...ch }); setChForm(ch); }} data-testid={`channel-edit-${ch.id}`} className="p-2 border border-white/15 hover:bg-white/10 transition-colors"><Edit3 size={14} /></button>
                      <button onClick={() => deleteChannel(ch)} data-testid={`channel-delete-${ch.id}`} className="p-2 border border-white/15 hover:bg-red-500/20 hover:border-red-500/40 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
                <button onClick={() => { setChData(CH_EMPTY); setChForm("new"); }} data-testid="channel-add-btn" className="flex items-center gap-2 bg-white text-black px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-white/90 active:scale-95 transition-all">
                  <Plus size={14} /> Add Channel
                </button>
              </>
            ) : (
              <form onSubmit={saveChannel} className="space-y-3" data-testid="channel-form">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Channel Name" value={chData.name} onChange={(v) => setChData({ ...chData, name: v })} testid="ch-name" required />
                  <Field label="Category" value={chData.category} onChange={(v) => setChData({ ...chData, category: v })} testid="ch-category" placeholder="Movies / Music / Live" />
                </div>
                <div>
                  <label className="font-mono-imn text-[10px] tracking-widest uppercase text-white/50">Stream Type</label>
                  <select data-testid="ch-source-type" className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:border-gold mt-1" value={chData.source_type} onChange={(e) => setChData({ ...chData, source_type: e.target.value })}>
                    <option className="bg-black" value="hls">HLS Stream (.m3u8)</option>
                    <option className="bg-black" value="youtube">YouTube Live (embed URL)</option>
                  </select>
                </div>
                <Field label="Stream URL" value={chData.source_url} onChange={(v) => setChData({ ...chData, source_url: v })} testid="ch-url" placeholder={chData.source_type === "hls" ? "https://.../master.m3u8" : "https://www.youtube.com/embed/LIVE_ID?autoplay=1"} />
                <div>
                  <Field label="Logo URL" value={chData.logo} onChange={(v) => setChData({ ...chData, logo: v })} testid="ch-logo" />
                  <label className="mt-2 inline-flex items-center gap-2 text-[11px] font-mono-imn uppercase tracking-widest text-gold cursor-pointer hover:opacity-80">
                    <Upload size={13} /> {uploadingLogo ? "Uploading…" : "Or upload a logo"}
                    <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="ch-logo-upload" onChange={(e) => e.target.files[0] && uploadChannelLogo(e.target.files[0])} />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3 items-center">
                  <Field label="Order" value={chData.order} onChange={(v) => setChData({ ...chData, order: parseInt(v) || 0 })} testid="ch-order" />
                  <label className="flex items-center gap-2 text-sm cursor-pointer mt-5" data-testid="ch-live-toggle">
                    <input type="checkbox" checked={chData.is_live} onChange={(e) => setChData({ ...chData, is_live: e.target.checked })} className="accent-white" />
                    On Air
                  </label>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setChForm(null)} className="flex-1 border border-white/20 py-3 text-sm uppercase tracking-wider hover:bg-white/10 transition-colors">Cancel</button>
                  <button type="submit" data-testid="channel-save-btn" className="flex-1 bg-white text-black font-semibold py-3 text-sm uppercase tracking-wider hover:bg-white/90 active:scale-[0.98] transition-all">Save Channel</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {waitlistOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 overflow-y-auto">
          <div className="relative w-full max-w-lg border border-white/10 bg-[#0A0A0A] p-8 my-8" data-testid="waitlist-modal">
            <button type="button" onClick={() => setWaitlistOpen(false)} data-testid="waitlist-close-btn" className="absolute right-4 top-4 text-white/50 hover:text-white">
              <X size={20} />
            </button>
            <h2 className="font-display text-3xl font-bold uppercase mb-1 flex items-center gap-2">
              <Bell size={22} className="text-gold" /> Waitlist
            </h2>
            <p className="text-white/40 text-xs mb-6">Fans signed up for launch alerts on your Coming Soon shows.</p>
            {!waitlist ? (
              <p className="text-white/40 text-sm py-8 text-center">Loading…</p>
            ) : (
              <>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="bg-gold text-black text-xs font-bold uppercase tracking-widest px-3 py-1.5">{waitlist.total} total</span>
                  {Object.entries(waitlist.by_show || {}).map(([show, n]) => (
                    <span key={show} className="border border-white/20 text-white/70 text-xs uppercase tracking-widest px-3 py-1.5">{show}: {n}</span>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 mb-5">
                  {Object.keys(waitlist.by_show || {}).map((show) => (
                    <button
                      key={show}
                      onClick={() => launchNotify(show)}
                      disabled={!!launching}
                      data-testid={`launch-${show}`}
                      className="flex items-center gap-1.5 bg-gold text-black text-[11px] font-semibold uppercase tracking-widest px-3 py-2 hover:opacity-90 active:scale-95 transition-all disabled:opacity-40"
                    >
                      <Bell size={12} /> {launching === show ? "Sending…" : `Notify "${show}" it's live`}
                    </button>
                  ))}
                  {waitlist.total > 0 && (
                    <button
                      onClick={() => launchNotify("all")}
                      disabled={!!launching}
                      data-testid="launch-all"
                      className="border border-gold text-gold text-[11px] font-semibold uppercase tracking-widest px-3 py-2 hover:bg-gold/10 transition-colors disabled:opacity-40"
                    >
                      {launching === "all" ? "Sending…" : "Notify Everyone"}
                    </button>
                  )}
                </div>
                <div className="border border-white/10 max-h-72 overflow-y-auto hide-scrollbar">
                  {waitlist.signups.length === 0 && <p className="p-4 text-white/40 text-sm text-center">No sign-ups yet.</p>}
                  {waitlist.signups.map((s, i) => (
                    <div key={i} data-testid="waitlist-row" className={`flex items-center justify-between gap-3 p-3 ${i !== waitlist.signups.length - 1 ? "border-b border-white/10" : ""}`}>
                      <div className="min-w-0">
                        <p className="text-sm truncate">{s.email}</p>
                        <p className="font-mono-imn text-[10px] tracking-widest uppercase text-white/40">{s.show}</p>
                      </div>
                      {s.email_status && (
                        <span className={`font-mono-imn text-[9px] uppercase tracking-widest px-2 py-0.5 border ${s.email_status === "sent" ? "border-gold/50 text-gold" : "border-white/20 text-white/40"}`}>{s.email_status}</span>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder, required }) {
  return (
    <div>
      <label className="font-mono-imn text-[10px] tracking-widest uppercase text-white/50">{label}</label>
      <input
        data-testid={testid}
        className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:border-gold transition-colors mt-1"
        value={value}
        placeholder={placeholder}
        required={required}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
